import { FC } from 'react';
import { Box, VStack, HStack, Heading, Text, Button } from '@chakra-ui/react';
import { FiDownload, FiExternalLink } from 'react-icons/fi';
import { AppLayout } from '@/components/layout/AppLayout';

/**
 * 操作マニュアル PDF の公開パス。
 * 実体は public/manual/ に置かれ、ビルド時に dist へそのままコピーされる。
 * PDF は `npm run manual:build` で再生成する（docs/manual/README.md 参照）。
 */
export const MANUAL_PDF_PATH = '/manual/operation-manual.pdf';

/** ダウンロード時に保存されるファイル名 */
export const MANUAL_DOWNLOAD_FILENAME = 'ブリーダー管理システム_操作マニュアル.pdf';

/**
 * 操作マニュアルページコンポーネント
 * PDF をページ内に埋め込んで表示し、別タブ表示とダウンロードも提供する。
 */
const ManualPage: FC = () => {
  return (
    <AppLayout>
      <Box maxW="6xl" mx="auto">
        <VStack gap={6} align="stretch">
          {/* ヘッダー */}
          <HStack justify="space-between" flexWrap="wrap" gap={4}>
            <Heading size="xl">操作マニュアル</Heading>
            <HStack gap={2}>
              <Button asChild variant="outline" size="sm">
                <a href={MANUAL_PDF_PATH} target="_blank" rel="noopener noreferrer">
                  <FiExternalLink size={16} />
                  <Text ml={2}>別タブで開く</Text>
                </a>
              </Button>
              <Button asChild colorPalette="blue" size="sm">
                <a href={MANUAL_PDF_PATH} download={MANUAL_DOWNLOAD_FILENAME}>
                  <FiDownload size={16} />
                  <Text ml={2}>ダウンロード</Text>
                </a>
              </Button>
            </HStack>
          </HStack>

          {/* 説明 */}
          <Box
            p={4}
            bg="blue.50"
            borderRadius="md"
            borderLeft="4px solid"
            borderLeftColor="blue.500"
          >
            <VStack align="start" gap={1}>
              <Text fontWeight="medium" color="blue.800">
                このシステムの使い方をまとめた操作マニュアルです
              </Text>
              <Text fontSize="sm" color="blue.600">
                各画面の使い方を、実際の画面のスクリーンショット付きで解説しています。
                印刷して手元に置いておくこともできます。
              </Text>
            </VStack>
          </Box>

          {/* PDF 埋め込み */}
          <Box
            bg="white"
            borderRadius="lg"
            shadow="md"
            overflow="hidden"
            h={{ base: '65vh', md: '80vh' }}
          >
            <iframe
              src={MANUAL_PDF_PATH}
              title="操作マニュアル"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          </Box>

          {/* 埋め込み表示に対応していない環境向けの案内 */}
          <Box p={3} bg="gray.50" borderRadius="md">
            <Text fontSize="sm" color="gray.600">
              PDF が表示されない場合は、スマートフォンなどブラウザが PDF
              の埋め込み表示に対応していない可能性があります。
              上の「別タブで開く」または「ダウンロード」からご覧ください。
            </Text>
          </Box>
        </VStack>
      </Box>
    </AppLayout>
  );
};

export default ManualPage;
